import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// Sprint 7 T7.1.e — Composant unifié pour les empty states.
// Cf maquettes :
//  - design/atomic-crm/contacts-app.jsx (.empty-state — vide / filtré vide)
//  - design/atomic-crm/home-app.jsx (.empty-celebrate — célébration)
//  - design/atomic-crm/contacts.css + home.css pour les tailles précises.
//
// Deux variantes visuelles :
// - "neutral" : ic-circle gris (#f4f2ec / muted), padding 64px 24px,
//   pour les états "rien à montrer" (vide total, filtres trop stricts)
// - "celebrate" : ic-circle vert pastel, padding 40px 24px, pour les
//   états positifs "tout est traité"

interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description?: string;
  ctas?: ReactNode;
  variant?: "neutral" | "celebrate";
  className?: string;
}

export const EmptyState = ({
  icon,
  title,
  description,
  ctas,
  variant = "neutral",
  className,
}: EmptyStateProps) => {
  const isCelebrate = variant === "celebrate";
  return (
    <div
      className={cn(
        "text-center",
        isCelebrate ? "py-10 px-6" : "py-16 px-6",
        className,
      )}
    >
      <div
        className={cn(
          "size-14 rounded-full flex items-center justify-center mx-auto mb-3.5",
          isCelebrate
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
            : "bg-muted text-muted-foreground",
        )}
      >
        {icon}
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1.5">
        {title}
      </h3>
      {description && (
        <p className="text-[13px] text-muted-foreground leading-relaxed mx-auto max-w-[380px] mb-4">
          {description}
        </p>
      )}
      {ctas && (
        <div className="flex flex-wrap gap-2 justify-center">{ctas}</div>
      )}
    </div>
  );
};
